import { Injectable } from '@angular/core';
import { IProgramInformation } from '@carm/_core';
import { mapCaseTypeToProgramTypes, mapProgramEnrolmentTypeToProgramTypes, mapProgramTypeToProgramEnrolmentType } from '@carm/_core/business-registration/converters/program-account-profile.converters';
import { ISecurityRequirementPaymentInfo } from '@carm/_core/financial-security/models/financial-security-api.model';
import { IFinancialSecurityEnrolment } from '@carm/_core/financial-security/models/financial-security.model';
import { PaymentContext } from '@carm/_core/payments/models';
import { mapResponseToProgramEnrolmentStatus } from '@carm/_core/program-enrolment/converters';
import { IHaveEnrolmentInformation, IProgramEnrolmentDraftResponse } from '@carm/_core/program-enrolment/models/enrol-program-api.model';
import { SubProgramEnrolmentType } from '@carm/_core/program-enrolment/models/enrol-program-enums.model';
import {
  getSecurityRequirementTypeBy,
  IProgramEnrolmentInfo,
} from '@carm/_core/program-enrolment/models/sub-program-enrolment.model';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Observable } from 'rxjs';
import {
  ReleasePriorPaymentStep,
} from '../components/enrolment/models/release-prior-payment.model';

@Injectable()
export class FinancialSecurityEnrolmentStateService {
  public previousUrl = null;
  private readonly releasePriorPaymentStepChangedSubject =
    new BehaviorSubject<ReleasePriorPaymentStep>(
      ReleasePriorPaymentStep.SecurityRequirement
    );
  private readonly requiredAmountFulfilledSubject = new BehaviorSubject<boolean>(false);
  private mainModel: IFinancialSecurityEnrolment;

  constructor(private translateService: TranslateService) {}

  public get releasePriorPaymentStepChanged$(): Observable<ReleasePriorPaymentStep> {
    return this.releasePriorPaymentStepChangedSubject.asObservable();
  }

  public get requiredAmountFulfilled$(): Observable<boolean> {
    return this.requiredAmountFulfilledSubject.asObservable();
  }

  public notifyStepChanged(step: ReleasePriorPaymentStep): void {
    this.releasePriorPaymentStepChangedSubject.next(step);
  }

  public notifyRequiredAmountFulfilled(value: boolean): void {
    this.requiredAmountFulfilledSubject.next(value);
  }

  public get financialSecurityForm(): IFinancialSecurityEnrolment {
    return this.mainModel;
  }

  public startProgramEnrolmentPayment(draft: IProgramEnrolmentDraftResponse, alreadyMapped: boolean = true): void {
    const enrolmentPayment = alreadyMapped ? draft : mapResponseToProgramEnrolmentStatus(draft, this.translateService);
    this.startEnrolment(enrolmentPayment, PaymentContext.Business);
  }

  public startOrphanProgramEnrolmentPayment(draft: IProgramEnrolmentDraftResponse, alreadyMapped: boolean = true): void {
    const enrolmentPayment = alreadyMapped ? draft : mapResponseToProgramEnrolmentStatus(draft, this.translateService);
    this.startEnrolment(enrolmentPayment, PaymentContext.Orphan);
  }

  public startSubProgramEnrolmentPayment(draft: IProgramEnrolmentDraftResponse, alreadyMapped: boolean = true): void {
    const enrolmentPayment = alreadyMapped ? draft : mapResponseToProgramEnrolmentStatus(draft, this.translateService);
    this.startEnrolment(enrolmentPayment, PaymentContext.Program);
  }

  public startRppPayment(draft: IHaveEnrolmentInformation, currentProgramProfile: IProgramInformation): void {
    const rppEnrolment = {
      draftId: draft.draftId,
      programAccountName: currentProgramProfile?.programName,
      programAccountNumber: currentProgramProfile?.programAccountNumber,
      programType: mapProgramTypeToProgramEnrolmentType(currentProgramProfile?.programType),
      subType: currentProgramProfile.programSubType,
      carrier: currentProgramProfile?.otherInformation,
    } as IHaveEnrolmentInformation;
    this.startEnrolment(rppEnrolment, PaymentContext.Program);
  }

  public startBondApplyPayment(draft: IProgramEnrolmentDraftResponse, currentProgramProfile: IProgramInformation): void {
    const bondApplyPayment = {
      ...mapResponseToProgramEnrolmentStatus(draft, this.translateService),
      programAccountName: currentProgramProfile?.programName,
      programAccountNumber: currentProgramProfile?.programAccountNumber,
      programType: mapCaseTypeToProgramTypes.get(draft?.caseType),
      subType: draft?.originalSubType,
      carrier: currentProgramProfile?.otherInformation,
    } as IHaveEnrolmentInformation;
    this.startEnrolment(bondApplyPayment, PaymentContext.Program, true);
  }

  public startCarrierApply(draft: IProgramEnrolmentDraftResponse, currentProgramProfile: IProgramInformation): void {
    const carrierApplyPayment = {
      draftId: draft.draftId,
      programAccountName: currentProgramProfile?.programName,
      programAccountNumber: currentProgramProfile?.programAccountNumber,
      programType: mapProgramTypeToProgramEnrolmentType(currentProgramProfile?.programType),
      subType:
        currentProgramProfile?.carrierMode ||
        currentProgramProfile?.carrierCategory ||
        currentProgramProfile?.warehouseType,
      carrier: currentProgramProfile?.otherInformation,
    } as IHaveEnrolmentInformation;
    this.startEnrolment(carrierApplyPayment, PaymentContext.Program, true);
  }

  private startEnrolment(
    data: IHaveEnrolmentInformation,
    paymentContext: PaymentContext,
    isBondedApply?: boolean
  ): void {
    const programType = mapProgramEnrolmentTypeToProgramTypes.get(
      data.programType
    );
    const program = {
      programAccountName: data.programAccountName,
      programAccountNumber: data.programAccountNumber,
      programType,
      programSubType: data.subType,
      isSubProgram: paymentContext === PaymentContext.Program,
      securityRequirementInfo: {
        amount: data.requiredBondAmount,
        licenseFee: data.licenseFee,
        carrier: data.carrier,
      } as ISecurityRequirementPaymentInfo,
    } as IProgramEnrolmentInfo;

    const requirementType = getSecurityRequirementTypeBy(
      programType,
      program.programSubType
    );
    this.mainModel = {
      draftId: data.draftId,
      caseNumber: data.caseNumber,
      program,
      subProgramType: SubProgramEnrolmentType.ReleasePriorPayment,
      securityRequirementType: requirementType,
      paymentContext,
      isBondedApply,
    } as IFinancialSecurityEnrolment;
  }

  public update(model: IFinancialSecurityEnrolment): void {
    this.mainModel = model;
  }

  public clear(): void {
    this.mainModel = null;
  }
}
